/**
 * CRMTradingPanel.js
 * 
 * НОВЫЙ КОМПОНЕНТ — вставить в TradingAccountDetail.js
 * 
 * Использование в TradingAccountDetail.js:
 *   import CRMTradingPanel from './CRMTradingPanel';
 *   // Добавить вкладку в существующие вкладки:
 *   <CRMTradingPanel clientId={account.client_id} accountId={accountId} />
 * 
 * Что умеет:
 *  - Показывает активные опционы и CFD позиции клиента
 *  - Изменение страйк-цены опциона
 *  - Закрытие опциона (win/loss/cancel)
 *  - Изменение SL/TP/объёма CFD
 *  - Принудительное закрытие CFD с указанием цены
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';
import { calcPnl as fmCalcPnl } from '../utils/financeMath';
import {
  Activity, Lock, RefreshCw,
  ArrowUpRight, ArrowDownRight, Target, Wallet,
} from 'lucide-react';

const API = `${getBackendUrl()}/api`;

const getToken = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

// Compact summary card used in the panel header
const SummaryCard = ({ icon, label, value, valueColor = '#111827', badgeBg = '#f1f5f9', iconColor = '#475569', sub }) => (
  <div style={{
    background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:'11px 13px',
    display:'flex', alignItems:'center', gap:11, boxShadow:'0 1px 2px rgba(15,23,42,.04)'
  }}>
    <div style={{ width:34, height:34, borderRadius:8, background:badgeBg, color:iconColor, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {icon}
    </div>
    <div style={{ minWidth:0 }}>
      <div style={{ fontSize:10, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'.04em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</div>
      <div style={{ fontSize:16, fontWeight:700, color:valueColor, fontVariantNumeric:'tabular-nums', lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>{sub}</div>}
    </div>
  </div>
);

const CRMTradingPanel = ({ clientId, accountId, onTabChange, optionsExtraTop, forceResultMode, onCloseOption, onModalStateChange }) => {
  const [tab, setTab]                 = useState('cfd');  // 'options' | 'cfd' — CFD по умолчанию (главный фокус)
  // Notify parent when tab changes (used to e.g. show Trading Settings only on options tab)
  useEffect(() => { if (onTabChange) onTabChange(tab); }, [tab, onTabChange]);
  const [optionTrades, setOptionTrades] = useState([]);
  const [optionHistory, setOptionHistory] = useState([]);
  // [CFD-2026-05-13] Mirror CFD's Open/Closed pill for option trades
  const [optStatusFilter, setOptStatusFilter] = useState('open'); // 'open' | 'closed'
  const [closingOptionId, setClosingOptionId] = useState(null);
  const [cfdPositions, setCfdPositions] = useState([]);
  const [cfdOpenAll, setCfdOpenAll] = useState([]);     // для агрегатов (открытые)
  const [cfdClosedAll, setCfdClosedAll] = useState([]); // для агрегатов (закрытые)
  // [CFD-2026-05] Show open + closed CFD trades; toggle via "Открытые/Закрытые" pills
  const [cfdStatusFilter, setCfdStatusFilter] = useState('open'); // 'open' | 'closed'
  const [loading, setLoading]         = useState(false);
  const [msg, setMsg]                 = useState(null);

  // Edit modals
  const [editOption, setEditOption]   = useState(null);
  const [editCFD, setEditCFD]         = useState(null);

  // Option form state
  const [optStrike, setOptStrike]     = useState('');
  const [optStatus, setOptStatus]     = useState('');
  const [optResult, setOptResult]     = useState('');

  // CFD form state
  const [cfdVol, setCfdVol]           = useState('');
  const [cfdEntry, setCfdEntry]       = useState(''); // [CFD-2026-05] strike/entry override
  const [cfdSpread, setCfdSpread]     = useState(''); // [CFD-2026-05] spread (informational)
  const [cfdSL,  setCfdSL]            = useState('');
  const [cfdTP,  setCfdTP]            = useState('');
  const [cfdClosePrice, setCfdClosePrice] = useState('');

  const notify = (text, type = 'ok') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchOptions = useCallback(async () => {
    try {
      const [act, hist] = await Promise.all([
        axios.get(`${API}/trading/admin/trades`, {
          params: { status: 'active', limit: 100 }, headers: authHeader(),
        }),
        axios.get(`${API}/trading/admin/trades`, {
          params: { status: 'completed', limit: 100 }, headers: authHeader(),
        }).catch(() => ({ data: [] })),
      ]);
      const filterByClient = (arr) => clientId ? arr.filter(t => t.client_id === clientId) : arr;
      setOptionTrades(filterByClient(act.data || []));
      setOptionHistory(filterByClient(hist.data || []));
    } catch {}
  }, [clientId]);

  const fetchCFD = useCallback(async () => {
    try {
      const [openAll, closedAll] = await Promise.all([
        axios.get(`${API}/cfd/admin/positions`, {
          params: { client_id: clientId, status: 'open' },
          headers: authHeader(),
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/cfd/admin/positions`, {
          params: { client_id: clientId, status: 'closed' },
          headers: authHeader(),
        }).catch(() => ({ data: [] })),
      ]);
      setCfdOpenAll(openAll.data || []);
      setCfdClosedAll(closedAll.data || []);
      setCfdPositions(cfdStatusFilter === 'open' ? (openAll.data || []) : (closedAll.data || []));
    } catch {}
  }, [clientId, cfdStatusFilter]);

  // [REFRESH-2026-05-19h] Use a ref for the latest "is any modal open" flag so
  // the interval callback can check it WITHOUT having editOption/editCFD in
  // the effect's dependency array. Previously listing them as deps caused
  // the effect to teardown + re-run on every modal open/close, which fired an
  // immediate extra fetchCFD() right when admin clicked "Изменить" — making
  // the page feel like it was reloading just as the modal opened.
  const modalOpenRef = useRef(false);
  useEffect(() => {
    modalOpenRef.current = Boolean(editOption || editCFD);
  }, [editOption, editCFD]);

  useEffect(() => {
    fetchOptions();
    fetchCFD();
    const t = setInterval(() => {
      if (modalOpenRef.current) return; // paused while editing
      fetchOptions();
      fetchCFD();
    }, 15000);
    return () => clearInterval(t);
  }, [fetchOptions, fetchCFD]);

  // [REFRESH-2026-05-19g] Bubble modal-open state up to the parent so the
  // outer page (TradingAccountDetail) can also pause its own 30s refresh.
  useEffect(() => {
    if (typeof onModalStateChange === 'function') {
      onModalStateChange(Boolean(editOption || editCFD));
    }
  }, [editOption, editCFD, onModalStateChange]);

  // Save option edit
  const saveOption = async () => {
    if (!editOption) return;
    setLoading(true);
    try {
      const payload = {};
      if (optStrike) payload.strike_price = parseFloat(optStrike);
      if (optResult) payload.profit_loss  = parseFloat(optResult);
      if (optStatus) payload.status       = optStatus;
      await axios.put(`${API}/trading/admin/trades/${editOption.id}`, payload, { headers: authHeader() });
      notify('Опцион обновлён');
      setEditOption(null);
      fetchOptions();
    } catch (e) {
      notify(e.response?.data?.detail || 'Ошибка', 'err');
    } finally {
      setLoading(false);
    }
  };

  // Save CFD edit
  // [CFD-2026-05] mode: 'save' | 'close' | 'close-sl' | 'close-tp' | 'reopen'
  const saveCFD = async (mode = 'save') => {
    if (!editCFD) return;
    setLoading(true);
    try {
      const payload = {};
      if (cfdVol) payload.volume      = parseFloat(cfdVol);
      if (cfdEntry) payload.entry_price = parseFloat(cfdEntry);
      if (cfdSpread !== '') payload.spread = parseFloat(cfdSpread);
      if (cfdSL)  payload.stop_loss   = parseFloat(cfdSL);
      if (cfdTP)  payload.take_profit = parseFloat(cfdTP);
      if (mode === 'close') {
        payload.force_close = true;
        if (cfdClosePrice) payload.close_price = parseFloat(cfdClosePrice);
      } else if (mode === 'close-sl') {
        payload.force_close = true;
        payload.close_price = parseFloat(cfdSL || cfdClosePrice);
      } else if (mode === 'close-tp') {
        payload.force_close = true;
        payload.close_price = parseFloat(cfdTP || cfdClosePrice);
      } else if (mode === 'reopen') {
        payload.reopen = true;
      }
      await axios.put(`${API}/cfd/admin/positions/${editCFD.id}`, payload, { headers: authHeader() });
      const msgs = { save:'Позиция обновлена', close:'Позиция закрыта', 'close-sl':'Закрыто по Stop Loss', 'close-tp':'Закрыто по Take Profit', reopen:'Позиция переоткрыта' };
      notify(msgs[mode] || 'Сохранено');
      setEditCFD(null);
      fetchCFD();
    } catch (e) {
      notify(e.response?.data?.detail || 'Ошибка', 'err');
    } finally {
      setLoading(false);
    }
  };

  // [CFD-2026-05] Live PnL preview helper — shows delta to client when admin
  // changes entry price or volume before saving.
  // [CFD-2026-05-12] Uses symbol-aware finance math (correct for cross pairs).
  // [CFD-2026-05-19e] "P/U сейчас" must always reflect the REAL current market
  // state (so admin sees what the client sees right now). "P/U после" uses the
  // admin-typed close price if provided, else current price. Previously both
  // values used the typed close price, hiding the real impact of the change.
  const computePnlPreview = () => {
    if (!editCFD) return null;
    const symStr = editCFD.symbol;
    const dir = editCFD.direction;
    // Current market state (snapshot when modal was opened — refetched via fetchCFD interval)
    const marketCur = parseFloat(editCFD.current_price) || parseFloat(editCFD.entry_price) || 0;
    // What the admin proposes as the close/check price (if any)
    const projectedCur = parseFloat(cfdClosePrice) || marketCur;
    const oldEntry = parseFloat(editCFD.entry_price) || 0;
    const oldVol = parseFloat(editCFD.volume) || 0;
    const newEntry = parseFloat(cfdEntry) || oldEntry;
    const newVol = parseFloat(cfdVol) || oldVol;
    const oldPnl = fmCalcPnl(symStr, dir, oldVol, oldEntry, marketCur);
    const newPnl = fmCalcPnl(symStr, dir, newVol, newEntry, projectedCur);
    return {
      old: oldPnl,
      next: newPnl,
      delta: newPnl - oldPnl,
      marketCur,
      projectedCur,
    };
  };

  // ─────────────── AGGREGATES for header summary ───────────────
  const summary = useMemo(() => {
    const openMargin = cfdOpenAll.reduce((s, p) => s + (parseFloat(p.margin) || 0), 0);
    const openUnrealized = cfdOpenAll.reduce((s, p) => s + (parseFloat(p.unrealized_pnl) || 0), 0);
    const closedRealized = cfdClosedAll.reduce((s, p) => s + (parseFloat(p.realized_pnl) || 0), 0);
    const optActive = optionTrades.length;
    const optTotal = optionTrades.length + optionHistory.length;
    const winRate = (() => {
      const won = optionHistory.filter(t => t.status === 'won').length;
      return optTotal > 0 ? Math.round((won / optTotal) * 100) : 0;
    })();
    return {
      cfdOpen: cfdOpenAll.length,
      cfdClosed: cfdClosedAll.length,
      openMargin,
      openUnrealized,
      closedRealized,
      optActive,
      optTotal,
      winRate,
    };
  }, [cfdOpenAll, cfdClosedAll, optionTrades, optionHistory]);

  // ─────────────────────────────────────────
  // STYLES
  // ─────────────────────────────────────────
  const inp = {
    width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 6,
    border: '1px solid #d1d5db', background: '#fff', color: '#111827',
    outline: 'none', marginBottom: 8,
  };
  const sel = { ...inp, cursor: 'pointer' };
  const btnPrimary = (color = '#2563eb') => ({
    padding: '8px 16px', borderRadius: 6, border: 'none',
    background: color, color: '#fff', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'opacity .15s',
  });
  const btnSecondary = {
    padding: '8px 16px', borderRadius: 6,
    border: '1px solid #d1d5db', background: '#fff', color: '#374151',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
  };

  const Badge = ({ up, label }) => (
    <span style={{
      padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: up ? '#dcfce7' : '#fee2e2', color: up ? '#14532d' : '#7f1d1d',
    }}>
      {label}
    </span>
  );

  return (
    <div style={{ fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', fontSize: 13 }}>

      {/* ── HEADER: Title + Summary cards ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#7c3aed,#2563eb)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(124,58,237,.35)' }}>
            <Activity size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', lineHeight:1.1 }}>Управление CFD сделками</div>
            <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>Открытые позиции, история и редактирование</div>
          </div>
        </div>
        <button onClick={() => { fetchOptions(); fetchCFD(); }} title="Обновить"
          style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', fontSize:12, fontWeight:600, border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', cursor:'pointer', color:'#475569' }}>
          <RefreshCw size={14} /> Обновить
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:10, marginBottom:16 }} data-testid="cfd-summary-cards">
        <SummaryCard icon={<Activity size={16} />} label="Открытых CFD" value={summary.cfdOpen} valueColor="#0f172a" badgeBg="#eef2ff" iconColor="#6366f1" />
        <SummaryCard icon={<Lock size={16} />} label="Маржа удерживается" value={`$${summary.openMargin.toFixed(2)}`} valueColor="#0f172a" badgeBg="#fef3c7" iconColor="#d97706" />
        <SummaryCard
          icon={summary.openUnrealized >= 0 ? <ArrowUpRight size={16}/> : <ArrowDownRight size={16}/>}
          label="Нереализованный P/L"
          value={`${summary.openUnrealized >= 0 ? '+' : ''}$${summary.openUnrealized.toFixed(2)}`}
          valueColor={summary.openUnrealized >= 0 ? '#16a34a' : '#dc2626'}
          badgeBg={summary.openUnrealized >= 0 ? '#dcfce7' : '#fee2e2'}
          iconColor={summary.openUnrealized >= 0 ? '#16a34a' : '#dc2626'}
        />
        <SummaryCard
          icon={<Wallet size={16} />}
          label="Реализован. P/L"
          value={`${summary.closedRealized >= 0 ? '+' : ''}$${summary.closedRealized.toFixed(2)}`}
          valueColor={summary.closedRealized >= 0 ? '#16a34a' : '#dc2626'}
          badgeBg={summary.closedRealized >= 0 ? '#dcfce7' : '#fee2e2'}
          iconColor={summary.closedRealized >= 0 ? '#16a34a' : '#dc2626'}
          sub={`${summary.cfdClosed} закрытых сделок`}
        />
        <SummaryCard icon={<Target size={16} />} label="Опционы" value={`${summary.optActive} / ${summary.optTotal}`} valueColor="#0f172a" badgeBg="#fce7f3" iconColor="#db2777" sub={`Win rate ${summary.winRate}%`} />
      </div>

      {/* Notification */}
      {msg && (
        <div style={{
          padding: '9px 12px', borderRadius: 7, marginBottom: 12, fontSize: 13, fontWeight: 500,
          background: msg.type === 'ok' ? '#dcfce7' : '#fee2e2',
          color: msg.type === 'ok' ? '#14532d' : '#7f1d1d',
          border: `1px solid ${msg.type === 'ok' ? '#16a34a50' : '#dc262650'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap:4, padding:4, background:'#f1f5f9', borderRadius:10, marginBottom: 14, width:'fit-content' }} data-testid="crm-trading-tabs">
        {[
          { key: 'cfd',     label: 'CFD позиции', count: summary.cfdOpen + summary.cfdClosed, icon: <Activity size={13} /> },
          { key: 'options', label: 'Опционы',     count: summary.optTotal,                    icon: <Target size={13} /> },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`crm-tab-${t.key}`} style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding: '8px 14px', fontSize: 12.5, border: 'none', cursor: 'pointer',
            background: tab === t.key ? '#fff' : 'transparent',
            color: tab === t.key ? '#0f172a' : '#64748b',
            fontWeight: tab === t.key ? 700 : 500,
            borderRadius: 7,
            boxShadow: tab === t.key ? '0 1px 3px rgba(15,23,42,.08)' : 'none',
            transition: 'all .12s',
          }}>
            {t.icon}
            <span>{t.label}</span>
            <span style={{ padding:'1px 7px', fontSize:11, fontWeight:700, borderRadius:10, background: tab===t.key ? '#eef2ff' : '#e2e8f0', color: tab===t.key ? '#4f46e5' : '#64748b', minWidth:18, textAlign:'center' }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ── OPTIONS ── */}
      {tab === 'options' && (() => {
        const isOpen = optStatusFilter === 'open';
        const list = isOpen ? optionTrades : optionHistory;
        return (
        <>
          {optionsExtraTop}
          {/* Open/Closed pill — same UX as CFD */}
          <div style={{display:'flex',gap:6,marginBottom:12,padding:3,background:'#f3f4f6',borderRadius:8,width:'fit-content'}} data-testid="opt-status-tabs">
            {[['open','Открытые'],['closed','История']].map(([id,lbl]) => (
              <button key={id} onClick={() => setOptStatusFilter(id)} data-testid={`opt-status-${id}`}
                style={{padding:'7px 14px',fontSize:12,fontWeight:600,borderRadius:6,border:'none',cursor:'pointer',
                  background: optStatusFilter===id ? '#fff' : 'transparent',
                  color: optStatusFilter===id ? '#111827' : '#6b7280',
                  boxShadow: optStatusFilter===id ? '0 1px 3px rgba(0,0,0,.08)' : 'none'}}>
                {lbl}
              </button>
            ))}
          </div>

          {list.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              {isOpen ? 'Нет активных опционов' : 'История пуста'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {(isOpen
                      ? ['Актив','Направление','Сумма','Страйк','Выплата','Истекает','Действия']
                      : ['Дата','Актив','Направление','Сумма','Длит.','Результат','П/У','Действия']
                    ).map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map(t => {
                    const closing = closingOptionId === t.id;
                    return (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }} data-testid={`opt-row-${t.id}`}>
                      {isOpen ? (
                        <>
                          <td style={{ padding: '9px 10px', fontWeight: 600, color: '#111827' }}>{t.asset}</td>
                          <td style={{ padding: '9px 10px' }}>
                            <Badge up={t.direction === 'call'} label={t.direction === 'call' ? '▲ ПОКУПКА' : '▼ ПРОДАЖА'} />
                          </td>
                          <td style={{ padding: '9px 10px', fontVariantNumeric: 'tabular-nums' }}>${t.amount?.toFixed(2)}</td>
                          <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontSize: 11 }}>{t.strike_price?.toFixed(5)}</td>
                          <td style={{ padding: '9px 10px', fontVariantNumeric: 'tabular-nums' }}>${t.expected_payout?.toFixed(2)}</td>
                          <td style={{ padding: '9px 10px', color: '#6b7280', fontSize: 11 }}>
                            {t.expires_at ? new Date(t.expires_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                            {/* Force-close buttons — only show "Win"/"Loss" when admin set force_result_mode='auto'.
                                Otherwise show single "Закрыть" (server decides). */}
                            {(forceResultMode === 'auto' || !forceResultMode) ? (
                              <>
                                <button disabled={closing || !onCloseOption}
                                  onClick={async () => { setClosingOptionId(t.id); try { await onCloseOption?.(t.id, 'win'); fetchOptions(); } finally { setClosingOptionId(null); } }}
                                  style={{ ...btnSecondary, padding: '4px 9px', fontSize: 10.5, marginRight: 5, background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0', opacity: closing ? .5 : 1 }}
                                  data-testid={`opt-close-win-${t.id}`}>
                                  Прибыль
                                </button>
                                <button disabled={closing || !onCloseOption}
                                  onClick={async () => { setClosingOptionId(t.id); try { await onCloseOption?.(t.id, 'loss'); fetchOptions(); } finally { setClosingOptionId(null); } }}
                                  style={{ ...btnSecondary, padding: '4px 9px', fontSize: 10.5, marginRight: 5, background: '#fee2e2', color: '#991b1b', borderColor: '#fecaca', opacity: closing ? .5 : 1 }}
                                  data-testid={`opt-close-loss-${t.id}`}>
                                  Убыток
                                </button>
                              </>
                            ) : (
                              <button disabled={closing || !onCloseOption}
                                onClick={async () => { setClosingOptionId(t.id); try { await onCloseOption?.(t.id, null); fetchOptions(); } finally { setClosingOptionId(null); } }}
                                style={{ ...btnSecondary, padding: '4px 9px', fontSize: 10.5, marginRight: 5, opacity: closing ? .5 : 1 }}
                                data-testid={`opt-close-${t.id}`}>
                                Закрыть
                              </button>
                            )}
                            <button onClick={() => {
                              setEditOption(t);
                              setOptStrike(t.strike_price?.toString() || '');
                              setOptStatus(''); setOptResult('');
                            }} style={{ ...btnSecondary, padding: '4px 9px', fontSize: 10.5 }} data-testid={`opt-edit-${t.id}`}>
                              Изменить
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '9px 10px', color: '#6b7280', fontSize: 11 }}>
                            {t.created_at ? new Date(t.created_at).toLocaleString('ru-RU', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                          </td>
                          <td style={{ padding: '9px 10px', fontWeight: 600, color: '#111827' }}>{t.asset}</td>
                          <td style={{ padding: '9px 10px' }}>
                            <Badge up={t.direction === 'call'} label={t.direction === 'call' ? '▲ ПОКУПКА' : '▼ ПРОДАЖА'} />
                          </td>
                          <td style={{ padding: '9px 10px', fontVariantNumeric: 'tabular-nums' }}>${t.amount?.toFixed(2)}</td>
                          <td style={{ padding: '9px 10px', fontSize: 11, color: '#6b7280' }}>{t.duration_seconds ? `${Math.round(t.duration_seconds/60)}м` : '—'}</td>
                          <td style={{ padding: '9px 10px' }}>
                            <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                              background: t.status === 'won' ? '#dcfce7' : '#fee2e2',
                              color: t.status === 'won' ? '#166534' : '#991b1b' }}>
                              {t.status === 'won' ? 'ВЫИГРАЛ' : t.status === 'lost' ? 'ПРОИГРАЛ' : t.status}
                            </span>
                          </td>
                          <td style={{ padding: '9px 10px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: (t.profit_loss||t.profit||0) >= 0 ? '#16a34a' : '#dc2626' }}>
                            {(t.profit_loss||t.profit||0) >= 0 ? '+' : ''}${(t.profit_loss||t.profit||0).toFixed(2)}
                          </td>
                          <td style={{ padding: '9px 10px' }}>
                            <button onClick={() => {
                              setEditOption(t);
                              setOptStrike(t.strike_price?.toString() || '');
                              setOptStatus(t.status || ''); setOptResult('');
                            }} style={{ ...btnSecondary, padding: '4px 9px', fontSize: 10.5 }} data-testid={`opt-edit-${t.id}`}>
                              Изменить
                            </button>
                          </td>
                        </>
                      )}
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
        );
      })()}

      {/* ── CFD POSITIONS ── */}
      {tab === 'cfd' && (
        <>
          {/* [CFD-2026-05] Open/Closed toggle */}
          <div style={{display:'flex',gap:6,marginBottom:12,padding:3,background:'#f3f4f6',borderRadius:8,width:'fit-content'}} data-testid="cfd-status-tabs">
            {[
              ['open','Открытые'],
              ['closed','Закрытые'],
            ].map(([id,lbl]) => (
              <button key={id} onClick={()=>setCfdStatusFilter(id)} data-testid={`cfd-status-${id}`}
                style={{padding:'7px 14px',fontSize:12,fontWeight:600,borderRadius:6,border:'none',cursor:'pointer',
                  background: cfdStatusFilter===id ? '#fff' : 'transparent',
                  color: cfdStatusFilter===id ? '#111827' : '#6b7280',
                  boxShadow: cfdStatusFilter===id ? '0 1px 3px rgba(0,0,0,.08)' : 'none'}}>
                {lbl}
              </button>
            ))}
          </div>
          {cfdPositions.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            {cfdStatusFilter==='open' ? 'Нет открытых CFD позиций' : 'Нет закрытых CFD позиций'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {(cfdStatusFilter==='open'
                    ? ['Символ','Направление','Объём','Вход','Тек.цена','П/У','Маржа','SL','TP','Действия']
                    : ['Символ','Направление','Объём','Вход','Закрытие','П/У','Маржа','Закрыто','Действия']
                  ).map(h => (
                    <th key={h} style={{ padding: '8px 9px', textAlign: 'left', fontSize: 11, color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cfdPositions.map(pos => {
                  const isClosed = pos.status === 'closed';
                  const pnl = isClosed ? (pos.realized_pnl || 0) : (pos.unrealized_pnl || 0);
                  return (
                    <tr key={pos.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '9px 9px', fontWeight: 600, color: '#111827' }}>{pos.symbol}</td>
                      <td style={{ padding: '9px 9px' }}>
                        <Badge up={pos.direction === 'buy'} label={pos.direction === 'buy' ? '▲ BUY' : '▼ SELL'} />
                      </td>
                      <td style={{ padding: '9px 9px', fontVariantNumeric: 'tabular-nums' }}>{pos.volume?.toFixed(2)}</td>
                      <td style={{ padding: '9px 9px', fontFamily: 'monospace', fontSize: 11 }}>{pos.entry_price?.toFixed(5)}</td>
                      <td style={{ padding: '9px 9px', fontFamily: 'monospace', fontSize: 11 }}>
                        {isClosed ? (pos.close_price?.toFixed(5) || '—') : (pos.current_price?.toFixed(5) || '—')}
                      </td>
                      <td style={{ padding: '9px 9px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: pnl >= 0 ? '#16a34a' : '#dc2626' }}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                      </td>
                      <td style={{ padding: '9px 9px', fontVariantNumeric: 'tabular-nums' }}>${pos.margin?.toFixed(2)}</td>
                      {!isClosed && (
                        <>
                          <td style={{ padding: '9px 9px', fontFamily: 'monospace', fontSize: 11, color: '#dc2626' }}>{pos.stop_loss?.toFixed(4) || '—'}</td>
                          <td style={{ padding: '9px 9px', fontFamily: 'monospace', fontSize: 11, color: '#16a34a' }}>{pos.take_profit?.toFixed(4) || '—'}</td>
                        </>
                      )}
                      {isClosed && (
                        <td style={{ padding: '9px 9px', fontSize: 11, color: '#6b7280' }}>
                          {pos.closed_at ? new Date(pos.closed_at).toLocaleString('ru-RU', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                        </td>
                      )}
                      <td style={{ padding: '9px 9px' }}>
                        <button onClick={() => {
                          setEditCFD(pos);
                          setCfdVol(pos.volume?.toString() || '');
                          setCfdEntry(pos.entry_price?.toString() || '');
                          setCfdSpread(pos.spread != null ? pos.spread.toString() : '');
                          setCfdSL(pos.stop_loss?.toString() || '');
                          setCfdTP(pos.take_profit?.toString() || '');
                          setCfdClosePrice((isClosed ? pos.close_price : pos.current_price)?.toString() || '');
                        }} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11 }} data-testid={`cfd-edit-${pos.id}`}>
                          {isClosed ? 'Редактировать' : 'Изменить'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
      )}

      {/* ── MODAL: Edit option ── */}
      {editOption && (
        <div style={{ minHeight: 380, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 16, borderRadius: 10 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 360, border: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 14 }}>
              Изменить опцион: {editOption.asset}
            </div>
            {/* [CFD-2026-05-19e] Strike-price input removed per user request:
                опционы не должны иметь редактируемую страйк-цену — она фиксируется
                в момент открытия по рынку. Если в CRM нужно изменить исход — админ
                использует поле «Результат P&L» или «Статус» ниже. */}
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, fontWeight: 600 }}>
              Страйк-цена <span style={{ color: '#9ca3af', fontWeight: 500 }}>(зафиксирована)</span>
            </div>
            <div style={{
              padding: '7px 10px', borderRadius: 6, background: '#f3f4f6',
              border: '1px solid #e5e7eb', color: '#374151',
              fontFamily: 'monospace', fontSize: 13, marginBottom: 8,
              fontVariantNumeric: 'tabular-nums',
            }} data-testid="opt-strike-readonly">
              {editOption.strike_price?.toFixed(5) ?? '—'}
            </div>

            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, fontWeight: 600 }}>Результат P&L</div>
            <input type="number" value={optResult} placeholder="Напр: 85 (победа) или -100 (проигрыш)"
              onChange={e => setOptResult(e.target.value)} style={inp} />

            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, fontWeight: 600 }}>Статус</div>
            <select value={optStatus} onChange={e => setOptStatus(e.target.value)} style={sel}>
              <option value="">— не менять —</option>
              <option value="won">won — победа</option>
              <option value="lost">lost — проигрыш</option>
              <option value="cancelled">cancelled — отменить</option>
            </select>

            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={saveOption} disabled={loading} style={btnPrimary()}>
                {loading ? '...' : 'Сохранить'}
              </button>
              <button onClick={() => setEditOption(null)} style={btnSecondary}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Edit CFD ── */}
      {editCFD && (() => {
        const preview = computePnlPreview();
        return (
        <div style={{ minHeight: 540, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 16, borderRadius: 10 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 22, width: 460, border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,.12)' }} data-testid="cfd-edit-modal">
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 4 }}>
              CFD позиция: {editCFD.symbol}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
              {editCFD.direction === 'buy' ? '▲ Покупка' : '▼ Продажа'} · вход {editCFD.entry_price?.toFixed(5)} · объём {editCFD.volume?.toFixed(2)} · маржа ${editCFD.margin?.toFixed(2)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Цена входа (страйк)</div>
                <input type="number" value={cfdEntry} step="0.00001" placeholder={editCFD.entry_price?.toFixed(5)}
                  data-testid="cfd-entry-input"
                  onChange={e => setCfdEntry(e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Объём (лоты)</div>
                <input type="number" value={cfdVol} step="0.01" min="0.01"
                  data-testid="cfd-volume-input"
                  onChange={e => setCfdVol(e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Спред (пипс)</div>
                <input type="number" value={cfdSpread} step="0.0001" placeholder={editCFD.spread != null ? String(editCFD.spread) : '—'}
                  data-testid="cfd-spread-input"
                  onChange={e => setCfdSpread(e.target.value)} style={inp} />
              </div>
              <div>
                {/* [LABEL-2026-05-19g] On open trades this is NOT the realized
                    close price (the trade is still live) — it's just the price
                    used for the P/U preview AND for the "Закрыть позицию" button.
                    Renamed/explained so admins don't think the trade is already
                    closed at this price. */}
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>
                  {editCFD.status === 'closed' ? 'Цена закрытия' : 'Цена для закрытия / preview'}
                </div>
                <input type="number" value={cfdClosePrice} step="0.0001" placeholder={editCFD.current_price?.toFixed(5)}
                  data-testid="cfd-close-price-input"
                  onChange={e => setCfdClosePrice(e.target.value)} style={inp} />
                {editCFD.status !== 'closed' && (
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: -4, marginBottom: 6, lineHeight: 1.3 }}>
                    Подставлена текущая рыночная цена. Используется кнопкой «Закрыть позицию» и для предпросмотра П/У. Сделка остаётся открытой, пока не нажата кнопка закрытия.
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Stop Loss</div>
                <input type="number" value={cfdSL} step="0.0001" placeholder="—"
                  onChange={e => setCfdSL(e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Take Profit</div>
                <input type="number" value={cfdTP} step="0.0001" placeholder="—"
                  onChange={e => setCfdTP(e.target.value)} style={inp} />
              </div>
            </div>

            {/* [CFD-2026-05] Live PnL preview — how the change affects the client's position */}
            {preview && (
              <div data-testid="cfd-pnl-preview" style={{
                marginTop: 4, padding: '10px 12px', borderRadius: 8,
                background: '#f8fafc', border: '1px solid #e5e7eb',
                fontSize: 12, color: '#374151',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'center'
              }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 600 }}>P/U сейчас</div>
                  <div style={{ fontWeight: 700, color: preview.old >= 0 ? '#16a34a' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                    {preview.old >= 0 ? '+' : ''}${preview.old.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 600 }}>P/U после</div>
                  <div style={{ fontWeight: 700, color: preview.next >= 0 ? '#16a34a' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                    {preview.next >= 0 ? '+' : ''}${preview.next.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 600 }}>Изменение клиенту</div>
                  <div style={{
                    fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                    color: preview.delta >= 0 ? '#16a34a' : '#dc2626',
                    fontSize: 14
                  }}>
                    {preview.delta >= 0 ? '+' : ''}${preview.delta.toFixed(2)}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button onClick={() => saveCFD('save')} disabled={loading} style={btnPrimary()} data-testid="cfd-save-btn">
                {loading ? '...' : 'Сохранить изменения'}
              </button>
              {editCFD?.status !== 'closed' ? (
                <>
                  <button onClick={() => saveCFD('close')} disabled={loading} style={btnPrimary('#dc2626')} data-testid="cfd-force-close-btn">
                    {loading ? '...' : 'Закрыть позицию'}
                  </button>
                  {cfdSL && (
                    <button onClick={() => saveCFD('close-sl')} disabled={loading} style={{...btnSecondary,color:'#dc2626',borderColor:'#fecaca'}} data-testid="cfd-close-sl-btn">
                      Закрыть по SL
                    </button>
                  )}
                  {cfdTP && (
                    <button onClick={() => saveCFD('close-tp')} disabled={loading} style={{...btnSecondary,color:'#16a34a',borderColor:'#bbf7d0'}} data-testid="cfd-close-tp-btn">
                      Закрыть по TP
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => saveCFD('reopen')} disabled={loading} style={btnPrimary('#7c3aed')} data-testid="cfd-reopen-btn">
                  {loading ? '...' : 'Переоткрыть позицию'}
                </button>
              )}
              <button onClick={() => setEditCFD(null)} style={btnSecondary}>Отмена</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default CRMTradingPanel;
